import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './NotificationsTable.module.scss';
import CustomTable from '../../../Common/CustomTable';
import AvatarIcon from '../../../Common/AvatarIcon';
import { modalActions } from '../../../Modals/store';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { normalizeServerMessage } from 'utils/i18n';
import clx from 'classnames';
import { ChevronRight } from 'lucide-react';

const openClient = (dispatch, clientId) => {
  if (!clientId) return;
  dispatch(
    modalActions.OPEN_MODAL.SUCCESS({
      type: 'clientModal',
      props: { client_id: clientId },
    }),
  );
};

export const ServerNotice = ({ message }) => {
  const { t } = useTranslation();
  const norm = normalizeServerMessage(message);
  if (norm.raw) return t('notifications.serverMessage.server.raw', { message: norm.raw });
  return (
    <Trans
      i18nKey={`notifications.serverMessage.${norm.key}`}
      values={norm.params}
      components={{ bold: <b />, warning: <>⚠️</> }}
    />
  );
};

export default ({ values, setPaginationValue, pageCount, activeFilterId, isMobile }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const data = React.useMemo(() => values, [values]);
  const columns = React.useMemo(
    () =>
      isMobile
        ? [
            {
              Header: '',
              accessor: 'clientName',
              Cell: ({ row: { original } }) => {
                const dateValue = original.date || original.createdAt;
                const content = (
                  <>
                    <AvatarIcon name={original.clientName} icon={''} size="40px" />
                    <div className={styles.mobile_copy}>
                      <span className={styles.name}>{original.clientName}</span>
                      <span className={styles.meta}>
                        {dateValue ? dayjs(dateValue).format('DD/MM/YYYY · HH:mm') : ''}
                      </span>
                      <span className={styles.message}>
                        <ServerNotice message={original.message} />
                      </span>
                    </div>
                    {original.clientId ? <ChevronRight size={18} strokeWidth={2.2} /> : null}
                  </>
                );

                if (!original.clientId) {
                  return <div className={styles.notification_mobile_card}>{content}</div>;
                }

                return (
                  <button
                    type="button"
                    className={styles.notification_mobile_card}
                    aria-label={t('notifications.table.details')}
                    onClick={() => openClient(dispatch, original.clientId)}
                  >
                    {content}
                  </button>
                );
              },
            },
          ]
        : [
            {
              Header: t('notifications.table.headers.date'),
              accessor: 'createdAt',
              Cell: ({ row: { original } }) => {
                const dateValue = original.createdAt || original.date;
                return (
                  <div className={styles.date_cell}>
                    <span>{dateValue ? dayjs(dateValue).format('DD/MM/YYYY') : '—'}</span>
                    <span className={styles.date_time}>
                      {dateValue ? dayjs(dateValue).format('HH:mm') : ''}
                    </span>
                  </div>
                );
              },
            },
            {
              Header: t('notifications.table.headers.client'),
              accessor: 'client',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.name_cell_container}>
                    <AvatarIcon name={original.clientName} icon={''} size="40px" />
                    <span className={styles.name}>{original.clientName}</span>
                  </div>
                );
              },
            },
            {
              Header: t('notifications.table.headers.message'),
              accessor: 'message',
              Cell: ({ value }) => {
                return (
                  <div className={styles.message}>
                    <ServerNotice message={value} />
                  </div>
                );
              },
            },
            {
              Header: '',
              accessor: 'clientId',
              Cell: ({ value }) => {
                if (!value) return null;
                return (
                  <button
                    type="button"
                    className={styles.detail_btn}
                    onClick={() => openClient(dispatch, value)}
                  >
                    {t('notifications.table.details')}
                    <ChevronRight size={16} strokeWidth={2.2} />
                  </button>
                );
              },
            },
          ],
    [isMobile, t, dispatch],
  );

  return (
    <div
      className={clx(styles.table_card, {
        [styles.table_card_mobile]: isMobile,
      })}
    >
      <CustomTable
        data={data}
        columns={columns}
        serverSidePagination={true}
        setPaginationValue={setPaginationValue}
        pageCount={pageCount}
        entityId={activeFilterId}
        isMobile={isMobile}
      />
    </div>
  );
};
